import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS, ReactiveFormsModule, Validators, Validator, ValidationErrors } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CurrencyFormatterDirective } from '../../../core/directives/currency-formatter.directive';

@Component({
  selector: 'app-adjustable-amount',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: 'adjustable-amount.component.less',
  templateUrl: 'adjustable-amount.component.html',
  imports: [ReactiveFormsModule, CurrencyFormatterDirective, NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AdjustableAmountComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: AdjustableAmountComponent
    }
  ]
})
export class AdjustableAmountComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() amountLimit: number = 0;
  @Input() amountInCents: number = 0;
  @Input() isAdjustable: boolean = false;

  public get maxWholeAmount(): number {
    return this.amountLimit / 100; // Convert from cents to whole amount
  }
  private get maxWholeAmountLength(): number {
    return this.maxWholeAmount.toString().length;
  }

  form = this.fb.group({
    amount: new FormControl<number>(0, [Validators.min(0.01)])
  });

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form.controls.amount.setValue(this.amountInCents / 100); // Convert from cents to whole amount
    this.form.controls.amount.addValidators(Validators.max(this.maxWholeAmount));
    this.form.statusChanges.subscribe(() => {
      this.onChange(this.form.value);
      this.onTouched();
    });
  }

  get integerPart(): string {
    const input = document.querySelector('#wholeAmount') as HTMLInputElement;
    return this.integerPartOf(input.value) + ',';
  }

  get fractionalPart(): string {
    const input = document.querySelector('#wholeAmount') as HTMLInputElement;
    return this.fractionalPartOf(input.value);
  }

  get errorState(): boolean {
    return this.form.invalid && this.form.touched;
  }

  validate(): ValidationErrors | null {
    if (this.form.invalid) {
      return {
        wholeAmount: {
          valid: false,
          message: 'Invalid amount'
        }
      };
    }
    return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // The following ControlValueAccessor methods are required by the interface,
  // but do not need to be implemented for now.
  writeValue(obj: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  onAmountFocus(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input) {
      this.isAdjustable ? input.select() : input.blur();
    }
  }

  onWholeAmountBeforeInput(event: Event) {
    if (event instanceof InputEvent && event.data != null) {
      if (!this.isDigit(event.data) && !this.isSeparator(event.data)) {
        event.preventDefault();
      }

      const wholeAmount = (event.target as HTMLInputElement).value;
      const separatorFound = wholeAmount.match(/[.,]/);
      if (separatorFound) {
        if (this.isSeparator(event.data)) {
          event.preventDefault();
        }
        const fractionLength = wholeAmount.length - wholeAmount.indexOf(separatorFound[0]);
        if (fractionLength > 2 && !this.isTextSelected(event)) {
          event.preventDefault();
        }
      } else {
        const integerLength = wholeAmount.length;
        let isLengthLimitReached = integerLength == this.maxWholeAmountLength;
        if (isLengthLimitReached && !this.isTextSelected(event) && !this.isSeparator(event.data)) {
          event.preventDefault();
        }
      }
    }
  }

  get separatorRegexp(): RegExp {
    return /[.,]/;
  }

  private integerPartOf(value: string): string {
    const separatorFound = value.match(this.separatorRegexp);
    return separatorFound ? value.substring(0, separatorFound.index!) : value;
  }

  private fractionalPartOf(value: string): string {
    const separatorFound = value.match(this.separatorRegexp);
    return separatorFound ? value.substring(separatorFound.index! + 1) : '00';
  }

  private isTextSelected(event: Event): boolean {
    if (this.isAdjustable && event.target instanceof HTMLInputElement) {
      return event.target.selectionStart != event.target.selectionEnd;
    }
    return false;
  }

  private isDigit(input: string): boolean {
    return /^[0-9]$/i.test(input);
  }

  private isSeparator(input: string): boolean {
    return /[.,]/.test(input);
  }
}
